import React, { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { validateToken } from '../network/ApiEndpoint'
import useAuthentication from '../hooks/Authentication'
import useCompanyParam from '../hooks/CompanyParam'
import useCurrentUser from '../hooks/CurrentUser'
import { getCurrentLocale, activateDateSettings } from '../translations/i18n'
import { languageSelector } from '../redux-store/language/language.selectors'

import { AppRoutes } from './AppRoutes'
import { getChannel } from '../api/channel/getChannel'
import channelsActions from '../redux-store/channel/channels.actions'
import { useStateDataApi } from '../api/useStateDataApi'
import { getIsSpecialDomain } from '../getIsSpecialDomain'
import { setCustomTheme } from '../redux-store/channelTheme/channelTheme.actions'
import { getFontsForChannel } from '../api/channel/font/getFontsForChannel'
import {
  getChannelFonts,
  getChannelFontsError,
  getChannelFontsSuccess
} from '../redux-store/channelFonts/channelFonts.slice'
import { useRelevantTheme } from './useRelevantTheme'
import { useChannelTheme } from '../pages/ChannelPage/Theme/useChannelTheme'
import { useChannelDataStatus } from '../pages/ChannelPage/useChannelDataStatus'
import { channelSelector } from '../redux-store/channel/channel.selectors'
import authenticationActions from '../redux-store/authentication/authentication.actions'
import userActions from '../redux-store/user/user.actions'
import { useFeatureFlags } from '../featureFlags/useFeatureFlags'
import { FeatureFlagsAvailable } from '../featureFlags/FeatureFlagsAvailable'
import { useComingSoon } from './comingSoon/useComingSoon'
import { checkReleaseDate } from './comingSoon/checkReleaseDate'
import { ComingSoon } from './comingSoon/ComingSoon'
import { getChannelAbout } from 'api/channel/getChannelAbout'
import {
  getChannelAboutError,
  getChannelAboutRequest,
  getChannelAboutSuccess
} from 'redux-store/channelAbout/channelAbout.slice'

import './App.scss'
import { useFavIcon } from 'pages/ChannelPage/useFavIcon'
import languageActions from 'redux-store/language/language.actions'
import { isViewingStreamSelector } from 'redux-store/userInterface/userInterface.selectors'
import { locationActions } from 'redux-store/location/location.action'
import { userInterface } from 'redux-store/userInterface/userInterface.actions'
import { NativePlugins } from 'features/NativePlugins'
import { ConditionalRender } from 'components/ConditionalRender/ConditionalRender'
import notificationActions from 'redux-store/native/notification/notification.actions'
import { NATIVE_APP_HELPER } from 'constants/native_app_helper.constants'

import { PublicRoutes as NativeAppPublicRoutes } from 'routes/native/PublicRoutes'
import { ProtectedRoutes as NativeAppProtectedRoutes } from 'routes/native/ProtectedRoutes'
import { MainLayout } from './layouts/MainLayout'
import { ContentLayout } from './layouts/ContentLayout'
import { AppLayout } from './layouts/AppLayout'
import { OnBoardingProvider } from 'pages/native/onBoarding/context/OnBoardingContext'

// This an a lot of the logic inside of this function will need to be moved to a separate file
// Will not do it right now as the purpose of this is to show how to use the native plugin
const initializeNativePlugins = async () => {
  if (!NATIVE_APP_HELPER.IS_NATIVE_PLATFORM) return
  NativePlugins.GesturePlugin.addListener('edge_pan', (event) => {
    console.log(event)
    window.history.back()
  })
}

initializeNativePlugins()

const App: React.FC = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const company = useCompanyParam()
  const channel = useSelector(channelSelector)
  const { channelThemeIsLoading } = useChannelTheme()
  const {
    channelDataIsReady,
    channelDataNotFound,
    channelShouldChange,
    channelDataExpected
  } = useChannelDataStatus(channel, company, () =>
    dispatch(channelsActions.setShouldChange)
  )

  useFavIcon({ faviconUrl: channel?.data?.favicon_url })
  const isViewingStream = useSelector(isViewingStreamSelector)

  const { setFlag, getFlag } = useFeatureFlags()

  const currentLanguage = useSelector(languageSelector)
  const currentUser = useCurrentUser()
  const isViewerPage = /\/(g|watch)\//.test(location.pathname)

  const [locale] = useState(currentUser?.locale || getCurrentLocale())
  const [isChildOfThemedRoot, setIsChildOfThemedRoot] = useState(false)
  const initialComingSoonAttributes: ComingSoon = {
    logoUrl: undefined,
    backgroundImageUrl: undefined,
    vanityUrl: undefined,
    channelSlug: undefined
  }
  const [comingSoonAttributes, setComingSoonAttributes] = useState(
    initialComingSoonAttributes
  )
  useEffect(() => {
    if (isViewingStream && !isViewerPage) {
      dispatch(userInterface.closeViewStream())
    }
  }, [isViewerPage, isViewingStream])

  const [parentReleaseDate, setParentReleaseDate] = useState<
    number | undefined
  >(undefined)

  const [showLoading, setShowLoading] = useState<boolean>(
    channelShouldChange || true
  )
  const { loggingIn } = useAuthentication()

  useEffect(() => {
    validateToken()
      .then((isValid) => {
        if (isValid) {
          dispatch(authenticationActions.resetFromLocalState())
          dispatch(userActions.resetFromLocalState())
        }
      })
      .catch(() => {
        console.log('Error validating token')
      })

    dispatch(notificationActions.checkNotificationAvailability())
  }, [])

  const wrappedGetChannel = useCallback(getChannel, [])
  useStateDataApi({
    actions: {
      start: channelsActions.getChannel,
      success: channelsActions.channelCompanyActionCreator,
      error: channelsActions.getChannelError
    },
    apiCall: wrappedGetChannel,
    parameters: company
  })

  const wrappedGetAboutChannel = useCallback(getChannelAbout, [
    channelDataIsReady,
    company
  ])
  useStateDataApi({
    actions: {
      start: getChannelAboutRequest,
      success: getChannelAboutSuccess,
      error: getChannelAboutError
    },
    apiCall: wrappedGetAboutChannel,
    parameters: channel?.data?.parent_channel_subdomain || company
  })

  useEffect(() => {
    if (
      (channelDataIsReady && !channelThemeIsLoading && !channelShouldChange) ||
      channelDataNotFound
    ) {
      setShowLoading(false)
    } else {
      setShowLoading(true)
    }
  }, [
    channelDataNotFound,
    channelDataIsReady,
    channelThemeIsLoading,
    channelShouldChange
  ])
  useEffect(() => {
    if (!currentLanguage) {
      dispatch(languageActions.setLanguage(locale))
    }
  }, [locale])
  useEffect(() => {
    if (location) {
      dispatch(locationActions.setLocation({ pathName: location.pathname }))
    }
  }, [location])
  // eslint-disable-next-line no-unneeded-ternary
  const makeFontApiCall = channel?.data?.is_root_channel ? true : false
  const wrappedGetChannelFonts = useCallback(getFontsForChannel, [])
  useStateDataApi({
    actions: {
      start: getChannelFonts,
      success: getChannelFontsSuccess,
      error: getChannelFontsError
    },
    apiCall: wrappedGetChannelFonts,
    parameters: company,
    makeCall: makeFontApiCall
  })

  checkReleaseDate(
    channel,
    parentReleaseDate,
    setParentReleaseDate,
    setComingSoonAttributes
  )

  const isRootOrChild = channel?.data?.isRootChannel || isChildOfThemedRoot
  const now = Math.floor(Date.now() / 1000)

  useRelevantTheme({
    company,
    dispatch,
    channel,
    setParentReleaseDate,
    setComingSoonAttributes,
    setIsChildOfThemedRoot,
    setCustomTheme
  })

  useEffect(() => {
    activateDateSettings(locale)
  }, [currentUser, currentLanguage])

  const releaseDate = parentReleaseDate
  const releaseDateHasNotPassed = releaseDate && releaseDate > now

  const { hostname } = window.location
  const isSpecialDomain = getIsSpecialDomain(hostname)
  const hideComingSoonPage = getFlag(FeatureFlagsAvailable.HideComingSoon)

  useComingSoon(
    releaseDate,
    releaseDateHasNotPassed,
    isSpecialDomain,
    hideComingSoonPage,
    setFlag,
    channel
  )

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search)
    if (urlSearchParams.get('open_auth_popup') === 'register') {
      dispatch(authenticationActions.openLoginLayer(undefined, 'signup'))
    }
  }, [])
  const comingSoon = getFlag(FeatureFlagsAvailable.ComingSoon)

  return (
    <AppLayout
      comingSoon={comingSoon}
      comingSoonAttributes={comingSoonAttributes}
      releaseDate={releaseDate}
    >
      <ConditionalRender mobileApp={false}>
        <MainLayout
          channel={channel}
          loggingIn={loggingIn}
          showLoading={showLoading}
          isRootOrChild={isRootOrChild}
          isViewingStream={isViewingStream}
          channelDataIsReady={channelDataIsReady}
        >
          <AppRoutes
            channelDataIsReady={channelDataIsReady}
            channelDataExpected={channelDataExpected}
            channelDataNotFound={channelDataNotFound}
          />
        </MainLayout>
      </ConditionalRender>
      <ConditionalRender web={false}>
        {!NATIVE_APP_HELPER.SOLID_SPORT_APP.IS_CURRENT_DOMAIN && (
          <ContentLayout isLoading={showLoading}>
            <OnBoardingProvider companyName={company}>
              <NativeAppPublicRoutes company={company} />
            </OnBoardingProvider>
          </ContentLayout>
        )}

        <NativeAppProtectedRoutes company={company}>
          <>
            {/* <Switch>
              <Route path="/:company?/feed">
                <div>Feed page</div>
              </Route>
            </Switch> */}
            <MainLayout
              channel={channel}
              loggingIn={loggingIn}
              showLoading={showLoading}
              isRootOrChild={isRootOrChild}
              isViewingStream={isViewingStream}
              channelDataIsReady={channelDataIsReady}
            >
              <AppRoutes
                channelDataIsReady={channelDataIsReady}
                channelDataExpected={channelDataExpected}
                channelDataNotFound={channelDataNotFound}
              />
            </MainLayout>
          </>
        </NativeAppProtectedRoutes>
      </ConditionalRender>
    </AppLayout>
  )
}
export default App
